import { Eater } from '../types'
import { debugLog } from '../utils/log-helper'

/**
 * Status for earn ways rows
 */
export type EarnWayStatus = 'earned' | 'available' | 'claimed' | 'disabled'

/**
 * Action type for earn ways (for routing/action handling)
 */
export type EarnWayAction = 
  | 'how-to-earn-points'
  | 'signup-and-earn'
  | 'upload-receipt'
  | 'invite-a-restaurant'
  | 'leave-a-review'
  | 'refer-and-earn'
  | 'get-others-join-via-groups'
  | 'make-a-reservation'
  | 'view-restaurant-page'
  | 'view-restaurant-dishes'
  | 'share-restaurants-to-others'
  | 'login-and-use-app'
  | 'subscribe-to-newsletter'
  | 'follow-us-on-instagram'

/**
 * UI row model for ways to earn points
 */
export interface EarnWayRow {
  key: string // Stable ID
  title: string // Left text
  ctaText: string // Right-side button label
  status: EarnWayStatus // earned | available | claimed | disabled
  onClickAction?: EarnWayAction // String enum for routing/action
}

/**
 * Builds the "Ways to earn points" list from eaterDTO
 * 
 * This adapter function uses backend-driven fields from eaterDTO:
 * - eaterDTO.earnWays: Array of earn way definitions from backend
 * - eaterDTO.earnWayStatus: Record mapping earn way keys to earned/claimed status
 * 
 * If backend fields are not available, falls back to default list.
 * 
 * @param eaterDTO - The eater data object from backend
 * @returns Array of EarnWayRow objects for UI rendering
 */
export function buildEarnWays(eaterDTO: Eater | null | undefined): EarnWayRow[] {
  // Use backend earnWays if available
  if (eaterDTO?.earnWays && Array.isArray(eaterDTO.earnWays) && eaterDTO.earnWays.length > 0) {
    const earnWayStatus = eaterDTO.earnWayStatus ?? {}

    // Honor the backend `visible` flag — a way explicitly marked visible:false (e.g.
    // subscription_purchased, hidden so the list doesn't read like a sales funnel) must not render.
    // Only an explicit `false` hides it; undefined/true stay visible for backward compatibility.
    const visibleEarnWays = eaterDTO.earnWays.filter((way) => way.visible !== false)

    // Sort by sortOrder if available, otherwise maintain backend order
    const sortedEarnWays = [...visibleEarnWays].sort((a, b) => {
      if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
        return a.sortOrder - b.sortOrder
      }
      if (a.sortOrder !== undefined) return -1
      if (b.sortOrder !== undefined) return 1
      return 0
    })
    
    // Map backend earnWays to EarnWayRow format
    return sortedEarnWays.map((way) => {
      const isEarned = earnWayStatus[way.key]
      const isInstagramFollow = way.key === 'instagram_follow' || way.key === 'follow-us-on-instagram'
      const isComingSoon = way.key === 'make_reservation'
      
      // Determine status and CTA text
      let status: EarnWayStatus = 'available'
      let ctaText = way.ctaText ?? 'Earn points'
      
      if (isComingSoon) {
        status = 'disabled'
        ctaText = 'Coming soon'
      } else if (isEarned) {
        if (isInstagramFollow) {
          status = 'claimed'
          ctaText = 'Bonus claimed'
        } else {
          status = 'earned'
          ctaText = 'Earned'
        }
      }
      
      // Map key to onClickAction if it matches known actions
      const onClickAction = mapKeyToAction(way.key)
      
      return {
        key: way.key,
        title: way.title,
        ctaText,
        status,
        onClickAction,
      }
    })
  }
  
  // Fallback to default list if backend data not available
  if (process.env.NODE_ENV !== 'production') {
    debugLog('[buildEarnWays] Backend earnWays not available, using fallback list. This should be removed once backend is fully deployed.')
  }
  
  return getDefaultEarnWays(eaterDTO)
}

/**
 * Maps backend earn way key to onClickAction enum
 */
function mapKeyToAction(key: string): EarnWayAction | undefined {
  const keyToActionMap: Record<string, EarnWayAction> = {
    'how-to-earn-points': 'how-to-earn-points',
    'signup-and-earn': 'signup-and-earn',
    'upload-receipt': 'upload-receipt',
    'invite-a-restaurant': 'invite-a-restaurant',
    'leave-a-review': 'leave-a-review',
    'refer-and-earn': 'refer-and-earn',
    'get-others-join-via-groups': 'get-others-join-via-groups',
    'group_invite': 'get-others-join-via-groups',
    'make-a-reservation': 'make-a-reservation',
    'make_reservation': 'make-a-reservation',
    'view-restaurant-page': 'view-restaurant-page',
    'view-restaurant-dishes': 'view-restaurant-dishes',
    'share-restaurants-to-others': 'share-restaurants-to-others',
    'login-and-use-app': 'login-and-use-app',
    'subscribe-to-newsletter': 'subscribe-to-newsletter',
    'follow-us-on-instagram': 'follow-us-on-instagram',
    'instagram_follow': 'follow-us-on-instagram',
  }
  
  return keyToActionMap[key]
}

/**
 * Default earn ways list (used when backend earnWays is not available)
 * Uses legacy logic based on eaterDTO fields for backward compatibility
 * 
 * TODO: Remove this fallback once backend is fully deployed and all users have earnWays data.
 * This should only be used in development or when backend data is unavailable.
 */
function getDefaultEarnWays(eaterDTO?: Eater | null): EarnWayRow[] {
  if (process.env.NODE_ENV !== 'production') {
    debugLog('[getDefaultEarnWays] Using fallback earn ways. Backend earnWays not available. This should be removed once backend is fully deployed.')
  }

  return [
    {
      key: 'how-to-earn-points',
      title: 'How to earn points',
      ctaText: 'Read and Earn Up to 100 points',
      status: 'available',
      onClickAction: 'how-to-earn-points',
    },
    {
      key: 'signup-and-earn',
      title: 'Signup and earn',
      ctaText: 'Earn Up to 50 points',
      status: 'available',
      onClickAction: 'signup-and-earn',
    },
    {
      key: 'upload-receipt',
      // Coming soon: the backend marks this earn way "Upload a Receipt - Coming Soon". Keep the
      // fallback in sync so a missing backend earnWays payload can never prematurely expose the
      // receipt-upload flow as active. No onClickAction, so it stays inert even if rendered.
      title: 'Upload receipt',
      ctaText: 'Coming soon',
      status: 'disabled',
    },
    {
      key: 'invite-a-restaurant',
      title: 'Invite a restaurant',
      ctaText: 'Invite & Earn Up to 50 points',
      status: 'available',
      onClickAction: 'invite-a-restaurant',
    },
    {
      key: 'leave-a-review',
      title: 'Leave a review',
      ctaText: 'Earn Up to 5 points',
      status: 'available',
      onClickAction: 'leave-a-review',
    },
    {
      key: 'refer-and-earn',
      title: 'Refer & earn',
      ctaText: 'Invite and Earn Up to 10 points',
      status: 'available',
      onClickAction: 'refer-and-earn',
    },
    {
      key: 'get-others-join-via-groups',
      title: 'Get others join via groups',
      ctaText: 'Earn Up to 10 points',
      status: 'available',
      onClickAction: 'get-others-join-via-groups',
    },
    {
      key: 'view-restaurant-page',
      title: 'View restaurant page',
      ctaText: 'Earn Up to 5 points',
      status: 'available',
      onClickAction: 'view-restaurant-page',
    },
    {
      key: 'view-restaurant-dishes',
      title: 'View restaurant dishes',
      ctaText: 'Earn Up to 5 points',
      status: 'available',
      onClickAction: 'view-restaurant-dishes',
    },
    {
      key: 'share-restaurants-to-others',
      title: 'Share restaurants to others',
      ctaText: 'Earn Up to 7 points',
      status: 'available',
      onClickAction: 'share-restaurants-to-others',
    },
    {
      key: 'login-and-use-app',
      title: 'Login and use app',
      ctaText: 'Earn Up to 50 points',
      status: 'available',
      onClickAction: 'login-and-use-app',
    },
    {
      key: 'subscribe-to-newsletter',
      title: 'Subscribe to newsletter',
      ctaText: 'Earn Up to 50 points',
      status: 'available',
      onClickAction: 'subscribe-to-newsletter',
    },
    {
      key: 'follow-us-on-instagram',
      title: 'Engage With Us on Instagram',
      ctaText: 'Earn Up to 50 points',
      status: 'available',
      onClickAction: 'follow-us-on-instagram',
    },
  ]
}

